import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import {
  BatchGetDescendantsForEntityUrnsOptions,
  GetConsumersRequest,
  GetConsumersResponse,
  GetDataContractRequest,
  GetDataContractResponse,
  GetDescendantsForEntityUrnsRequest,
  GetConsumableDescriptorRequest,
  GetDescriptorResponse,
  GetProductDescriptorRequest,
  MarketplaceApi,
  RequestOptions,
} from './api';
import crossFetch from 'cross-fetch';
import {
  CustomError,
  MultipleCustomErrors,
  encodeQueryParams,
  handleFailedResponse,
} from '@agilelab/plugin-wb-platform-common';
import { EntityWithConsumableDescendants } from './types';
import {
  FacetRequest,
  FacetResponse,
  FilterDefinition,
  MarketplaceSearchRequestInput,
  MarketplaceSearchResponseByCategory,
  ProductMapSearchRequest,
  ProductMapSearchResponse,
  SortDefinition,
} from './search/schema';
import {
  ActivateBusinessConceptModelRequest,
  BusinessConceptModelDetail,
  BusinessConceptModelSummary,
  BusinessConceptModelValidationIssue,
  CloneBusinessConceptModelRequest,
  CreateBusinessConceptModelRequest,
  DeleteBusinessConceptModelRequest,
  GetActiveBusinessConceptModelRequest,
  GetBusinessConceptModelRequest,
  ListBusinessConceptModelsRequest,
  UpdateBusinessConceptModelRequest,
} from './businessConceptModel';

/**
 * A frontend and backend compatible client for communicating with the Marketplace
 * service.
 *
 * @public
 */
export class MarketplaceClient implements MarketplaceApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly baseUrlPromise: Promise<string>;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi?: { fetch: typeof fetch };
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
    this.baseUrlPromise = this.discoveryApi.getBaseUrl('marketplace');
  }

  async getBaseUrl() {
    return this.baseUrlPromise;
  }

  private async searchRequest<ResponseBody>(
    path: string,
    options?: RequestOptions,
    body?: unknown,
  ): Promise<ResponseBody> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      signal: options?.signal,
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    await handleFailedResponse(response);
    return response.json();
  }

  getSearchFilters(options?: RequestOptions): Promise<FilterDefinition[]> {
    return this.searchRequest('/v1/search/filters', options);
  }

  getSearchSorts(options?: RequestOptions): Promise<SortDefinition[]> {
    return this.searchRequest('/v1/search/sorts', options);
  }

  getSearchFacets(
    request: FacetRequest,
    options?: RequestOptions,
  ): Promise<FacetResponse> {
    return this.searchRequest('/v1/search/facets', options, request);
  }

  search<Category extends MarketplaceSearchRequestInput['category']>(
    request: MarketplaceSearchRequestInput & { category: Category },
    options?: RequestOptions,
  ): Promise<MarketplaceSearchResponseByCategory[Category]> {
    return this.searchRequest('/v1/search', options, request);
  }

  searchProductMap(
    request: ProductMapSearchRequest,
    options?: RequestOptions,
  ): Promise<ProductMapSearchResponse> {
    return this.searchRequest('/v1/product-map/search', options, request);
  }

  async instanceHasDocs(instanceId: string): Promise<boolean> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/documentation/static/${encodeURIComponent(instanceId)}`,
      {
        method: 'HEAD',
      },
    );
    return response.status === 200;
  }

  async getInstanceDocs(instanceId: string, path: string): Promise<string> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/documentation/static/${encodeURIComponent(
        instanceId,
      )}/${encodeURIComponent(path)}`,
      {
        method: 'GET',
      },
    );

    await handleFailedResponse(response);
    return response.text();
  }

  async rerouteStaticAssetBaseUrl(
    oldBaseUrl: string,
    instanceId: string,
    path: string,
  ): Promise<string> {
    const baseUrl = await this.baseUrlPromise;

    const newBaseUrl = `${baseUrl}/v1/documentation/static/${encodeURIComponent(
      instanceId,
    )}/${path}`;

    const newUrl = new URL(
      oldBaseUrl,
      newBaseUrl.endsWith('/') ? newBaseUrl : `${newBaseUrl}/`,
    ).toString();

    return newUrl;
  }

  async getConsumableDescendantsForEntityUrns(
    request: GetDescendantsForEntityUrnsRequest,
    options?: BatchGetDescendantsForEntityUrnsOptions,
  ): Promise<EntityWithConsumableDescendants[]> {
    const { entityUrns, environment } = request;
    const baseUrl = await this.baseUrlPromise;
    const chunkSize = options?.maxItemsPerRequest || 1000;

    const chunks = [];
    for (let i = 0; i < entityUrns.length; i += chunkSize) {
      chunks.push(entityUrns.slice(i, i + chunkSize));
    }

    const results = await Promise.all(
      chunks.map(async chunk => {
        const response = await this.fetchApi.fetch(
          `${baseUrl}/descendantsByUrns`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(options?.token && {
                Authorization: `Bearer ${options.token}`,
              }),
            },
            body: JSON.stringify({
              entityUrns: chunk,
              environment,
            }),
          },
        );
        await handleFailedResponse(response);
        return response.json() as Promise<EntityWithConsumableDescendants[]>;
      }),
    );

    return results.flat();
  }

  async getConsumers(
    request: GetConsumersRequest,
    options?: RequestOptions,
  ): Promise<GetConsumersResponse> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = {
      environment: request.environment,
      includeChildren: request.includeChildren,
    };
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/consumers/projects/reading-from/${encodeURIComponent(
        request.externalId,
      )}${encodeQueryParams(queryParams)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getDataContract(
    request: GetDataContractRequest,
    options?: RequestOptions,
  ): Promise<GetDataContractResponse | undefined> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = {
      environment: request.identifier.environment,
    };
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/data-contracts/${encodeURIComponent(
        request.identifier.externalId,
      )}${encodeQueryParams(queryParams)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    if (response.status === 404) return undefined;
    await handleFailedResponse(response);
    return response.json();
  }

  async postReview(
    projectId: number,
    review: {
      score: number;
      short_comment?: string;
      long_comment: string;
    },
    options?: RequestOptions,
  ): Promise<void> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/reviews`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
      body: JSON.stringify({ projectId, review }),
    });
    await handleFailedResponse(response);
  }

  async getProductDescriptor(
    request: GetProductDescriptorRequest,
    options?: RequestOptions,
  ): Promise<GetDescriptorResponse | undefined> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = { environment: request.environment };
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/products/${encodeURIComponent(
        request.urn,
      )}/descriptor${encodeQueryParams(queryParams)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    if (response.status === 404) return undefined;
    await handleFailedResponse(response);
    return response.json();
  }

  async getConsumableDescriptor(
    request: GetConsumableDescriptorRequest,
    options?: RequestOptions,
  ): Promise<GetDescriptorResponse | undefined> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = { environment: request.environment };
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/consumables/${encodeURIComponent(
        request.urn,
      )}/descriptor${encodeQueryParams(queryParams)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    if (response.status === 404) return undefined;
    await handleFailedResponse(response);
    return response.json();
  }

  /**
   * Handles a Business Concept Model response error, surfacing per-field
   * validation issues via `MultipleCustomErrors` so a form/drawer can keep
   * entered metadata and highlight the offending field(s).
   */
  private async handleFailedBusinessConceptModelResponse(
    response: Response,
  ): Promise<void> {
    if (response.ok) return;

    let json: any = null;
    try {
      json = await response.json();
    } catch (error) {
      throw new CustomError(
        'Unable to parse the response received',
        (error as Error).message,
      );
    }

    if (json && 'details' in json) {
      const issues: BusinessConceptModelValidationIssue[] = Array.isArray(
        json.details?.issues,
      )
        ? json.details.issues
        : [];
      throw new MultipleCustomErrors(
        issues.map(issue => ({
          message: issue.message,
          path: issue.path ? [issue.path] : undefined,
        })),
      );
    }

    throw new CustomError(json?.message ?? 'Unknown error', '');
  }

  async listBusinessConceptModels(
    _request: ListBusinessConceptModelsRequest,
    options?: RequestOptions,
  ): Promise<BusinessConceptModelSummary[]> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models`,
      {
        method: 'GET',
        headers: {
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getBusinessConceptModel(
    request: GetBusinessConceptModelRequest,
    options?: RequestOptions,
  ): Promise<BusinessConceptModelDetail | undefined> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models/${encodeURIComponent(request.id)}`,
      {
        method: 'GET',
        headers: {
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    if (response.status === 404) return undefined;
    await handleFailedResponse(response);
    return response.json();
  }

  async createBusinessConceptModel(
    request: CreateBusinessConceptModelRequest,
    options?: RequestOptions,
  ): Promise<BusinessConceptModelDetail> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
        body: JSON.stringify(request),
      },
    );
    await this.handleFailedBusinessConceptModelResponse(response);
    return response.json();
  }

  async updateBusinessConceptModel(
    request: UpdateBusinessConceptModelRequest,
    options?: RequestOptions,
  ): Promise<BusinessConceptModelDetail> {
    const { id, ...body } = request;
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
        body: JSON.stringify(body),
      },
    );
    await this.handleFailedBusinessConceptModelResponse(response);
    return response.json();
  }

  async deleteBusinessConceptModel(
    request: DeleteBusinessConceptModelRequest,
    options?: RequestOptions,
  ): Promise<void> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models/${encodeURIComponent(request.id)}`,
      {
        method: 'DELETE',
        headers: {
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    await this.handleFailedBusinessConceptModelResponse(response);
  }

  async cloneBusinessConceptModel(
    request: CloneBusinessConceptModelRequest,
    options?: RequestOptions,
  ): Promise<BusinessConceptModelDetail> {
    const { id, ...body } = request;
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models/${encodeURIComponent(id)}/clone`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
        body: JSON.stringify(body),
      },
    );
    await this.handleFailedBusinessConceptModelResponse(response);
    return response.json();
  }

  async activateBusinessConceptModel(
    request: ActivateBusinessConceptModelRequest,
    options?: RequestOptions,
  ): Promise<BusinessConceptModelDetail> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models/${encodeURIComponent(
        request.id,
      )}/activate`,
      {
        method: 'PUT',
        headers: {
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    await this.handleFailedBusinessConceptModelResponse(response);
    return response.json();
  }

  async getActiveBusinessConceptModel(
    _request: GetActiveBusinessConceptModelRequest,
    options?: RequestOptions,
  ): Promise<BusinessConceptModelDetail> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/business-concept-models/active`,
      {
        method: 'GET',
        headers: {
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    await this.handleFailedBusinessConceptModelResponse(response);
    return response.json();
  }
}
